import axios from 'axios';
import { Utils } from '@sbs/uikit-constructor';
import { BASE_URI_DEVELOPMENT_CENTER } from '@/api/CONST.js';

const prefix = BASE_URI_DEVELOPMENT_CENTER;

/**
 * @param {*} activityId
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns {Promise<AxiosResponse<any>>}
 */
export const getActivityQuestions = (activityId, config) => axios.get(`${prefix}/activities/${activityId}/questions`, config);

/**
 * add question to activity
 * @param {number|string} activityId activity id
 * @param {Array} questions
 * @returns Promise
 */
export const addPageActivityQuestion = (activityId, questions) => {
    const url = `${prefix}/activities/${activityId}/questions`;

    return axios.post(url, {
        questions,
    });
};

/**
 * copy activity question
 * @param {number|string} activityId activity id
 * @param {number|string} questionId question id
 * @returns Promise
 */
export const copyPageActivityQuestion = (activityId, questionId) => {
    const url = `${prefix}/activities/${activityId}/questions/${questionId}/copy`;

    return axios.post(url);
};

/**
 * change question of activity
 * @param {number|string} activityId activity id
 * @param {string|number} questionId question id
 * @param {object} questionData question data
 * @param {string} questionData.title question title
 * @param {string} questionData.description question description
 * @param {'OnlyChoice'|'MultipleChoice'|'GapsInTheText'|'Sorting'|'Matching'|'LikertScale'|'OpenQuestion'} questionData.type question type
 * @param {number} questionData.order question order for sort
 * @param {json} questionData.question_data object of question options in json
 * @returns Promise
 */
export const changePageActivityQuestion = (activityId, questionId, questionData, config) => {
    const url = `${prefix}/activities/${activityId}/questions/${questionId}`;

    return axios.put(url, questionData, config);
};

/**
 * remove activity question
 * @param {number|string} activityId activity id
 * @param {number|string} questionId question id
 * @returns Promise
 */
export const removePageActivityQuestion = (activityId, questionId) => {
    const url = `${prefix}/activities/${activityId}/questions/${questionId}`;

    return axios.delete(url);
};

/**
 * restore question of activity
 * @param {number|string} activityId activity id
 * @param {string|number} questionId question id
 * @returns Promise
 */
export const restorePageActivityQuestion = (activityId, questionId) => {
    const url = `${prefix}/activities/${activityId}/questions/${questionId}/restore`;

    return axios.post(url);
};

/**
 * sort question of activity
 * @param {number|string} activityId activity id
 * @param {string[]|number[]} sortArray array of question id's
 * @returns Promise
 */
export const sortPageActivityQuestion = (activityId, sortArray) => {
    const url = `${prefix}/activities/${activityId}/questions/sort`;

    return axios.post(url, { order: sortArray });
};

/**
 * add answer for activity question
 * @param {number|string} questionId question id
 * @param {object} answerData answer data
 * @param {string} answerData.title answer title
 * @param {1|0} answerData.is_correct is answer correct
 * @param {number} answerData.order answer order
 * @param {string} answerData.data answer text for question with other or free answer
 * @param {string} answerData.reactions_data answer reaction text
 * @returns Promise
 */
export const addPageActivityQuestionAnswer = (questionId, answerData) => {
    const url = `${prefix}/questions/${questionId}/answers`;

    return axios.post(url, answerData);
};

/**
 * change answer of activity question
 * @param {number|string} questionId question id
 * @param {number|string} answerId answer id
 * @param {object} answerData answer data
 * @param {string} answerData.title answer title
 * @param {1|0} answerData.is_correct is answer correct
 * @param {number} answerData.order answer order
 * @param {string} answerData.data answer text for question with other or free answer
 * @param {string} answerData.reactions_data answer reaction text
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns Promise
 */
export const changePageActivityQuestionAnswer = (questionId, answerId, answerData, config) => {
    const url = `${prefix}/questions/${questionId}/answers/${answerId}`;

    return axios.put(url, answerData, config);
};

/**
 * remove answer of activity question
 * @param {number|string} questionId question id
 * @param {number|string} answerId answer id
 * @returns Promise
 */
export const removePageActivityQuestionAnswer = (questionId, answerId) => {
    const url = `${prefix}/questions/${questionId}/answers/${answerId}`;

    return axios.delete(url);
};

/**
 * restore answer of activity question
 * @param {number|string} questionId question id
 * @param {number|string} answerId answer id
 * @returns Promise
 */
export const restorePageActivityQuestionAnswer = (questionId, answerId) => {
    const url = `${prefix}/questions/${questionId}/answers/${answerId}/restore`;

    return axios.post(url);
};

/**
 * sort answer of activity question
 * @param {number|string} questionId question id
 * @param {(number|string)[]} sortArray ordered array of answer ids
 * @returns Promise
 */
export const sortPageActivityQuestionAnswer = (questionId, sortArray) => {
    const url = `${prefix}/questions/${questionId}/answers/sort`;

    return axios.post(url, { order: sortArray });
};

/**
 * get test or quiz xls import file example
 * @param {number|string} activityId activity id
 * @returns Promise
 */
export const getPageActivityQuestionImportExample = activityId => {
    const url = `${prefix}/activities/${activityId}/questions/import_example`;

    return axios.get(url, { responseType: 'blob' });
};

/**
 * import activity questions from xlsx file
 * @param {number|string} activityId activity id
 * @param {File} file import xls file
 * @param {Function} [callback]
 * @returns Promise
 */
export const importPageActivityQuestions = (activityId, file, callback) => {
    const url = `${prefix}/activities/${activityId}/questions/import`;
    const formData = new FormData();

    formData.append('file', file, file.name);

    return axios.post(url, formData, {
        onUploadProgress: progressEvent => {
            if (Utils.Helpers.toRawType(callback) === 'Function') {
                callback(progressEvent);
            }
        },
    });
};

/**
 * export activity questions in xlsx file
 * @param {number|string} activityId activity id
 * @returns Promise
 */
export const exportPageActivityQuestions = activityId => {
    const url = `${prefix}/activities/${activityId}/questions/export`;

    return axios.get(url, { responseType: 'blob' });
};

/**
 * start export activity questions in xlsx file with answers
 * @param {number|string} activityId activity id
 * @returns Promise
 */
export const exportPageActivityQuestionsWithAnswers = activityId => axios.get(`${prefix}/activities/${activityId}/questions/export-user-answers`);

/**
 * start export students test result in xlsx file
 * @param {number|string} versionId version id
 * @param {number|string} activityId activity id
 * @returns Promise
 */
export const exportStudentsTestResult = ({ versionId, activityId }) => axios.get(`${prefix}/statistics/versions/${versionId}/test-by-students/${activityId}`);

/**
 * delete all answers for activity question
 * @param {number|string} questionId question id
 * @returns Promise
 */
export const removePageActivityQuestionAnswers = questionId => axios.delete(`${prefix}/questions/${questionId}/answers/delete-all`);

/**
 * @param activityId
 * @param {import('axios').AxiosRequestConfig} config
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const getTestSections = (activityId, config) => axios.get(`/content/api/development-center/activities/${activityId}/test-sections`, config);

/**
 * @param {{ sectionId,data, config?: import('axios').AxiosRequestConfig }} param
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const updateTestSection = ({
    sectionId,
    data,
    config,
}) => axios.put(`/content/api/development-center/test-sections/${sectionId}`, data, config);

/**
 * @param activityId
 * @param data
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const createTestSection = (activityId, data) => axios.post(`/content/api/development-center/activities/${activityId}/test-sections`, data);

/**
 * @param sectionId
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const deleteTestSection = sectionId => axios.delete(`/content/api/development-center/test-sections/${sectionId}`);

/**
 * @param {{ activityId, order }} params
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const sortTestSections = (params, config = {}) => {
    const { activityId, order } = params;

    return axios.post(`/content/api/development-center/activities/${activityId}/test-sections/sort`, {
        order,
    }, config);
};

/**
 * @param activityId
 * @param {{ target_test_section_id, questions }} data
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const moveQuestionsToSection = (activityId, data) => axios.post(`/content/api/development-center/activities/${activityId}/questions/move`, data);

/**
 * @param activityId
 * @param {{ sections, questions }} data
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const bulkRemoveSectionOrQuestions = (activityId, data) => axios.post(`/content/api/development-center/activities/${activityId}/test-sections/bulk-delete`, data);

/**
 * @param activityId
 * @param icon
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const applySectionsStyleIcon = (activityId, icon) => axios.patch(`/content/api/development-center/activities/${activityId}/test-sections/icon`, { icon });

/**
 * @param activityId
 * @param questionId
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const getQuestionRoutes = (activityId, questionId) => axios.get(`/content/api/development-center/activities/${activityId}/questions/${questionId}/routes`);

/**
 * @param activityId
 * @param questionId
 * @param routeId
 * @param data
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const updateQuestionRoute = (activityId, questionId, routeId, data) => axios.put(`/content/api/development-center/activities/${activityId}/questions/${questionId}/routes/${routeId}`, data);

/**
 * @param activityId
 * @param questionId
 * @param routeId
 * @param conditionId
 * @param data
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const updateRouteCondition = (activityId, questionId, routeId, conditionId, data) => axios.put(`/content/api/development-center/activities/${activityId}/questions/${questionId}/routes/${routeId}/conditions/${conditionId}`, data);

/**
 * @param activityId
 * @param questionId
 * @param routeId
 * @param {{ order, operator, answer_id }} data
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const createRouteCondition = (activityId, questionId, routeId, data) => axios.post(`/content/api/development-center/activities/${activityId}/questions/${questionId}/routes/${routeId}/conditions`, data);

/**
 * @param activityId
 * @param questionId
 * @param routeId
 * @param conditionId
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const deleteRouteCondition = (activityId, questionId, routeId, conditionId) => axios.delete(`/content/api/development-center/activities/${activityId}/questions/${questionId}/routes/${routeId}/conditions/${conditionId}`);
